import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { ChevronRight, Plus, ChevronsDownUp, ChevronsUpDown, EyeOff, Pencil, Trash2, Loader2, Download, CircleDot, CheckCircle, Circle, Clock, XCircle } from 'lucide-react';
import { useDocumentTitle } from 'usehooks-ts';
import { useQueryState, parseAsArrayOf, parseAsString } from 'nuqs';
import { toast } from 'sonner';
import { trpc } from '@/lib/trpc';
import { useDebounce } from '@/hooks/useDebounce';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useWorkspacePermissions } from '@/hooks/useWorkspacePermissions';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import MainHeader from '@/components/layout/MainHeader';
import { Main } from '@/components/main';
import { ControlTree, type ControlTreeRef } from '@/components/compliance/ControlTree';
import { TestsList } from '@/components/compliance/TestsList';
import { AddTestDrawer } from '@/components/compliance/AddTestDrawer';
import { ImportTemplateDrawer } from '@/components/compliance/ImportTemplateDrawer';
import { AssigneeFilter } from '@/components/compliance/AssigneeFilter';
import { LabelFilter } from '@/components/compliance/LabelFilter';
import { TestTypeFilter } from '@/components/compliance/TestTypeFilter';
import { TestsToolbar, type TestsToolbarStatusOption } from '@/components/compliance/TestsToolbar';
import type { ControlTreeNode, TaskDTO } from '@deepguide-ai/types';
import { controlTreeNodeToTaskDTO } from '@/lib/compliance-status';

const statusOptions: TestsToolbarStatusOption[] = [
  { value: 'all', label: 'All Status', icon: CircleDot, iconClass: 'text-muted-foreground' },
  { value: 'PASSED', label: 'Passed', icon: CheckCircle, iconClass: 'text-green-600 dark:text-green-400' },
  { value: 'FAILED', label: 'Failed', icon: XCircle, iconClass: 'text-red-600 dark:text-red-400' },
  { value: 'NEEDS_REVIEW', label: 'In Review', icon: Clock, iconClass: 'text-amber-600 dark:text-amber-400' },
  { value: 'IN_PROGRESS', label: 'In Progress', icon: Circle, iconClass: 'text-blue-600 dark:text-blue-400' },
  { value: 'NOT_TESTED', label: 'Not Started', icon: Circle, iconClass: 'text-muted-foreground' },
];

interface ControlFormData {
  controlId: string;
  title: string;
  description: string;
}

function ControlsExplorerPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { workspaceId, frameworkId } = Route.useParams();
  const { role, isAdmin } = useWorkspacePermissions(workspaceId)
  const isViewer = role === 'VIEWER'
  // State for view toggle (Tests vs Controls) - persisted in URL
  const [view, setView] = useQueryState('view', {
    defaultValue: 'tests',
    parse: (value) => (value === 'controls' ? 'controls' : 'tests'),
  });

  // State for filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedAssignee, setSelectedAssignee] = useState<string>('all');
  const [selectedTestType, setSelectedTestType] = useState<string>('all');
  const [hideNotApplicable, setHideNotApplicable] = useState(false);
  const [selectedLabelIds, setSelectedLabelIds] = useState<string[]>([]);
  const debouncedSearch = useDebounce(searchQuery, 200);

  // State for Create Test drawer
  const [addTestDrawerOpen, setAddTestDrawerOpen] = useState(false);
  const [selectedControlDefId, setSelectedControlDefId] = useState<string | null>(null);

  // State for Import Test drawer
  const [importTemplateDrawerOpen, setImportTemplateDrawerOpen] = useState(false);
  const [importControlDefId, setImportControlDefId] = useState<string | null>(null);

  // State for highlighting newly imported/created test
  const [highlightedStatusId, setHighlightedStatusId] = useState<string | null>(null);

  // Read expanded array from URL to extract statusId for highlighting (from test page navigation)
  const [expandedArray] = useQueryState(
    'expanded',
    parseAsArrayOf(parseAsString).withDefault([])
  );

  // On mount, check if expanded array contains a statusId to highlight
  // StatusIds are typically the last item and look like cuid format (starts with 'c' or 'cm', no '-')
  useEffect(() => {
    if (expandedArray.length > 0) {
      const lastItem = expandedArray[expandedArray.length - 1];
      // If it doesn't contain '-' and looks like a cuid (starts with 'c' or 'cm')
      if (lastItem && !lastItem.includes('-') && /^cm?[a-z0-9]+$/i.test(lastItem)) {
        setHighlightedStatusId(lastItem);
        // Clear highlight after 3 seconds (same as create/import)
        const timer = setTimeout(() => setHighlightedStatusId(null), 3000);
        return () => clearTimeout(timer);
      }
    }
  }, []); // Only on mount

  // State for Control Management
  const [isCreateControlDialogOpen, setIsCreateControlDialogOpen] = useState(false);
  const [editingControl, setEditingControl] = useState<string | null>(null);
  const [controlFormData, setControlFormData] = useState<ControlFormData>({
    controlId: '',
    title: '',
    description: '',
  });

  // State for Edit Framework dialog
  const [isEditFrameworkDialogOpen, setIsEditFrameworkDialogOpen] = useState(false);
  const [editFrameworkName, setEditFrameworkName] = useState('');
  const [editFrameworkDescription, setEditFrameworkDescription] = useState('');

  // State for Delete Framework confirmation dialog
  const [isDeleteFrameworkDialogOpen, setIsDeleteFrameworkDialogOpen] = useState(false);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState('');

  // Ref for ControlTree expand/collapse
  const controlTreeRef = useRef<ControlTreeRef>(null);
  const [isAllExpanded, setIsAllExpanded] = useState(false);

  // Track whether filters are active to sync isAllExpanded state
  useEffect(() => {
    const hasActiveFilter =
      debouncedSearch.trim().length > 0 ||
      selectedStatus !== 'all' ||
      selectedAssignee !== 'all';

    if (hasActiveFilter) {
      setIsAllExpanded(true);
    } else {
      setIsAllExpanded(false);
    }
  }, [debouncedSearch, selectedStatus, selectedAssignee]);

  const handleToggleExpandAll = useCallback(() => {
    if (controlTreeRef.current) {
      if (controlTreeRef.current.isAllExpanded) {
        controlTreeRef.current.collapseAll();
        setIsAllExpanded(false);
      } else {
        controlTreeRef.current.expandAll();
        setIsAllExpanded(true);
      }
    }
  }, []);

  // Fetch framework details
  const frameworkQuery = useQuery({
    ...trpc.guidedTemplate.getFramework.queryOptions({ id: frameworkId }),
    enabled: !!frameworkId,
  });

  // Fetch controls tree for this framework only
  const treeQuery = useQuery({
    ...trpc.compliance.getControlsTree.queryOptions({
      frameworkId,
      labelIds: selectedLabelIds.length > 0 ? selectedLabelIds : undefined,
    }),
    // Keep previous data while fetching to prevent flash
    placeholderData: (previousData) => (debouncedSearch.trim() ? undefined : previousData),
  });

  const framework = frameworkQuery.data;
  const tree = treeQuery.data?.tree || [];
  // Check if framework is built-in (no workspaceId means it's a system framework)
  const isBuiltInFramework = framework && !('workspaceId' in framework && framework.workspaceId);

  // Update document title with framework name
  useDocumentTitle(framework ? `${framework.name} Controls` : 'Controls Explorer');

  // Flatten tree to show categories directly (skip framework level since we're already on framework page)
  const categories = useMemo(
    () => (tree.length > 0 ? tree[0].children || [] : []),
    [tree]
  );

  // Filter categories by search query, status, and assignee
  const searchLower = debouncedSearch.trim().toLowerCase();
  const filteredTree = useMemo(
    () =>
      categories
        .map((category) => ({
          ...category,
          children: category.children
            ?.map((control) => {
              // Filter tests by status, assignee, and search
              let filteredTests = control.children || [];
              const controlIdLower = control.controlId?.toLowerCase() ?? '';
              const matchesControlId = searchLower ? controlIdLower.includes(searchLower) : false;

              // Status filter (on test level)
              if (selectedStatus !== 'all') {
                filteredTests = filteredTests.filter((test) => {
                  // Map filter values to test status values
                  if (selectedStatus === 'PASSED') return test.status === 'PASSED';
                  if (selectedStatus === 'FAILED') return test.status === 'FAILED';
                  if (selectedStatus === 'NEEDS_REVIEW') return test.status === 'NEEDS_REVIEW';
                  if (selectedStatus === 'NOT_TESTED') return test.status === 'NOT_TESTED' || !test.status;
                  return true;
                });
              }

              // Assignee filter
              if (selectedAssignee !== 'all') {
                filteredTests = filteredTests.filter((test) => {
                  if (selectedAssignee === 'unassigned') {
                    return !test.assignee;
                  }
                  if (selectedAssignee === 'assigned') {
                    return !!test.assignee;
                  }
                  return test.assignee?.id === selectedAssignee;
                });
              }

              if (searchLower && !matchesControlId) {
                filteredTests = filteredTests.filter((test) => {
                  const matchesName = test.name?.toLowerCase().includes(searchLower);
                  const matchesCode = (test as any).taskCode?.toLowerCase().includes(searchLower);
                  const matchesAssignee = test.assignee?.name?.toLowerCase().includes(searchLower);
                  return matchesName || matchesCode || matchesAssignee;
                });
              }

              return {
                ...control,
                children: filteredTests,
              };
            })
            .filter((control) => {
              // Search filter
              if (searchLower) {
                const matchesControl =
                  control.name.toLowerCase().includes(searchLower) ||
                  control.controlId?.toLowerCase().includes(searchLower);
                const matchesTests = control.children && control.children.length > 0;
                if (!matchesControl && !matchesTests) return false;
              }

              // If status or assignee filter is active, only show controls with matching tests
              if ((selectedStatus !== 'all' || selectedAssignee !== 'all') && (!control.children || control.children.length === 0)) {
                return false;
              }

              return true;
            }),
        }))
        .filter((category) => category.children && category.children.length > 0),
    [categories, searchLower, selectedAssignee, selectedStatus]
  );

  // ControlTree key to force remount when filters change (prevents stale nodes)
  const treeKey = useMemo(() => {
    const labelSignature = selectedLabelIds.join(',');
    return [searchLower, selectedStatus, selectedAssignee, hideNotApplicable ? '1' : '0', labelSignature].join('|');
  }, [hideNotApplicable, selectedLabelIds, searchLower, selectedAssignee, selectedStatus]);

  // Calculate overall stats for the framework
  const stats = useMemo(() => {
    // Control-level stats
    let totalControls = 0;
    let passedControls = 0;
    let failedControls = 0;
    let needsReviewControls = 0;
    let notTestedControls = 0;
    let notApplicableControls = 0;

    // Track unique tests by statusId to avoid counting duplicates
    // (same test can appear under multiple controls)
    const seenStatusIds = new Set<string>();
    let totalTests = 0;
    let passedTests = 0;
    let failedTests = 0;
    let inReviewTests = 0;
    let notStartedTests = 0;
    let assignedTests = 0;
    let unassignedTests = 0;

    categories.forEach((category) => {
      category.children?.forEach((control) => {
        // Count controls
        totalControls++;
        if (control.status === 'PASSED') passedControls++;
        else if (control.status === 'FAILED') failedControls++;
        else if (control.status === 'NEEDS_REVIEW') needsReviewControls++;
        else if (control.status === 'NOT_TESTED') notTestedControls++;
        else if (control.status === 'NOT_APPLICABLE') notApplicableControls++;

        // Count tests within each control
        control.children?.forEach((test) => {
          if (test.type === 'test' && test.statusId) {
            // Skip if we've already counted this test (dedup by statusId)
            if (seenStatusIds.has(test.statusId)) return;
            seenStatusIds.add(test.statusId);

            totalTests++;

            // Status counts
            if (test.status === 'PASSED') passedTests++;
            else if (test.status === 'FAILED') failedTests++;
            else if (test.status === 'NEEDS_REVIEW') inReviewTests++;
            else notStartedTests++; // NOT_TESTED or any other status

            // Assignment counts
            if (test.assignee) assignedTests++;
            else unassignedTests++;
          }
        });
      });
    });

    return {
      controls: {
        total: totalControls,
        passed: passedControls,
        failed: failedControls,
        needsReview: needsReviewControls,
        notTested: notTestedControls,
        notApplicable: notApplicableControls,
      },
      tests: {
        total: totalTests,
        passed: passedTests,
        failed: failedTests,
        inReview: inReviewTests,
        notStarted: notStartedTests,
        assigned: assignedTests,
        unassigned: unassignedTests,
      },
    };
  }, [categories]);

  // Extract flat list of tests from filtered tree for Tests view
  const flatTests = useMemo(() => {
    const tests: TaskDTO[] = [];
    const seenStatusIds = new Set<string>();

    const extractTests = (nodes: ControlTreeNode[], parentControlId?: string) => {
      for (const node of nodes) {
        if (node.type === 'test' && node.statusId) {
          // Dedupe by statusId (same test can appear under multiple controls)
          if (!seenStatusIds.has(node.statusId)) {
            seenStatusIds.add(node.statusId);
            tests.push(controlTreeNodeToTaskDTO(node, parentControlId));
          }
        }
        if (node.children) {
          // Pass controlId for control nodes to their test children
          const controlId = node.type === 'control' ? node.controlId : parentControlId;
          extractTests(node.children, controlId);
        }
      }
    };

    extractTests(filteredTree);

    // Sort: non-adhoc tests by displayId first, then adhoc tests at the end
    tests.sort((a, b) => {
      // Adhoc tests go to the end
      if (a.isAdHoc && !b.isAdHoc) return 1;
      if (!a.isAdHoc && b.isAdHoc) return -1;

      // Sort by displayId (test code like DEE-1, DEE-2)
      return (a.displayId || '').localeCompare(b.displayId || '', undefined, { numeric: true });
    });

    // Apply test type filter
    if (selectedTestType !== 'all') {
      return tests.filter((test) => {
        // Ad-hoc tests use AD_HOC type, others use their template type
        const testType = test.isAdHoc ? 'AD_HOC' : (test.templateType ?? 'GUIDED');
        return testType === selectedTestType;
      });
    }

    return tests;
  }, [filteredTree, selectedTestType]);

  const handleAddTest = (controlDefId: string) => {
    setSelectedControlDefId(controlDefId);
    setAddTestDrawerOpen(true);
  };

  const handleAddTestSuccess = async (statusId: string) => {
    // Refetch the controls tree to show the new test immediately
    await treeQuery.refetch();

    // Highlight the newly created test briefly
    setHighlightedStatusId(statusId);
    setTimeout(() => setHighlightedStatusId(null), 3000);
  };

  const handleImportTemplate = (controlDefId: string) => {
    setImportControlDefId(controlDefId);
    setImportTemplateDrawerOpen(true);
  };

  const handleImportTemplateSuccess = async (statusId: string) => {
    // Refetch the controls tree to show the new test immediately
    await treeQuery.refetch();

    // Highlight the newly imported test briefly
    setHighlightedStatusId(statusId);
    setTimeout(() => setHighlightedStatusId(null), 3000);
  };

  const markNotApplicableMutation = useMutation(
    trpc.complianceAnalytics.markControlNotApplicable.mutationOptions()
  );

  const markApplicableMutation = useMutation(
    trpc.complianceAnalytics.markControlApplicable.mutationOptions()
  );

  const handleMarkNotApplicable = async (controlDefId: string) => {
    // Find the control in the tree to get controlId
    let foundControl: { controlId: string } | null = null;

    for (const category of categories) {
      for (const control of category.children || []) {
        if (control.id === controlDefId) {
          foundControl = {
            controlId: control.controlId || '',
          };
          break;
        }
      }
      if (foundControl) break;
    }

    if (!foundControl) {
      toast.error('Control not found');
      return;
    }

    try {
      await markNotApplicableMutation.mutateAsync({
        frameworkId,
        controlId: foundControl.controlId,
      });

      toast.success(`${foundControl.controlId} marked as not applicable`);

      // Invalidate and refetch the controls tree
      await queryClient.invalidateQueries({
        queryKey: trpc.compliance.getControlsTree.queryKey({ frameworkId }),
      });
      await treeQuery.refetch();
    } catch (error) {
      console.error('Failed to mark control as not applicable', error);
      toast.error('Failed to mark control as not applicable');
    }
  };

  const handleMarkApplicable = async (controlDefId: string) => {
    // Find the control in the tree to get controlId
    let foundControl: { controlId: string } | null = null;

    for (const category of categories) {
      for (const control of category.children || []) {
        if (control.id === controlDefId) {
          foundControl = {
            controlId: control.controlId || '',
          };
          break;
        }
      }
      if (foundControl) break;
    }

    if (!foundControl) {
      toast.error('Control not found');
      return;
    }

    try {
      await markApplicableMutation.mutateAsync({
        frameworkId,
        controlId: foundControl.controlId,
      });

      toast.success(`${foundControl.controlId} marked as applicable`);

      // Invalidate and refetch the controls tree
      await queryClient.invalidateQueries({
        queryKey: trpc.compliance.getControlsTree.queryKey({ frameworkId }),
      });
      await treeQuery.refetch();
    } catch (error) {
      console.error('Failed to mark control as applicable', error);
      toast.error('Failed to mark control as applicable');
    }
  };

  // Control CRUD mutations
  const createControlMutation = useMutation({
    ...trpc.guidedTemplate.createControl.mutationOptions(),
    onSuccess: () => {
      toast.success('Control created successfully');
      queryClient.invalidateQueries({
        queryKey: trpc.compliance.getControlsTree.queryKey({ frameworkId }),
      });
      setIsCreateControlDialogOpen(false);
      setControlFormData({ controlId: '', title: '', description: '' });
    },
    onError: (error) => {
      toast.error('Failed to create control');
      console.error('Failed to create control:', error);
    },
  });

  const updateControlMutation = useMutation({
    ...trpc.guidedTemplate.updateControl.mutationOptions(),
    onSuccess: () => {
      toast.success('Control updated successfully');
      queryClient.invalidateQueries({
        queryKey: trpc.compliance.getControlsTree.queryKey({ frameworkId }),
      });
      setEditingControl(null);
      setControlFormData({ controlId: '', title: '', description: '' });
    },
    onError: (error) => {
      toast.error('Failed to update control');
      console.error('Failed to update control:', error);
    },
  });

  const deleteControlMutation = useMutation({
    ...trpc.guidedTemplate.deleteControl.mutationOptions(),
    onSuccess: () => {
      toast.success('Control deleted successfully');
      queryClient.invalidateQueries({
        queryKey: trpc.compliance.getControlsTree.queryKey({ frameworkId }),
      });
    },
    onError: (error) => {
      toast.error('Failed to delete control');
      console.error('Failed to delete control:', error);
    },
  });

  // Update framework mutation
  const updateFrameworkMutation = useMutation({
    ...trpc.guidedTemplate.updateFramework.mutationOptions(),
    onSuccess: () => {
      toast.success('Framework updated successfully');
      queryClient.invalidateQueries({
        queryKey: trpc.guidedTemplate.getFramework.queryKey({ id: frameworkId }),
      });
      setIsEditFrameworkDialogOpen(false);
    },
    onError: (error) => {
      toast.error(`Failed to update framework: ${error.message}`);
    },
  });

  // Open edit framework dialog
  const handleOpenEditFramework = () => {
    setEditFrameworkName(framework?.name || '');
    setEditFrameworkDescription(framework?.description || '');
    setIsEditFrameworkDialogOpen(true);
  };

  // Save framework changes
  const handleSaveFramework = () => {
    if (!editFrameworkName.trim()) {
      toast.error('Framework name is required');
      return;
    }
    updateFrameworkMutation.mutate({
      id: frameworkId,
      name: editFrameworkName.trim(),
      description: editFrameworkDescription.trim() || undefined,
    });
  };

  // Delete framework mutation
  const deleteFrameworkMutation = useMutation({
    ...trpc.guidedTemplate.deleteFramework.mutationOptions(),
    onSuccess: () => {
      toast.success('Framework deleted successfully');
      navigate({
        to: '/workspaces/$workspaceId/compliance',
        params: { workspaceId },
      });
    },
    onError: (error) => {
      toast.error(`Failed to delete framework: ${error.message}`);
    },
  });

  // Export Control Matrix mutation (Prescient audit format for this framework)
  const exportMutation = useMutation({
    ...trpc.compliance.exportPrescientFormat.mutationOptions(),
    onSuccess: (data) => {
      // Convert base64 to blob and trigger download
      const byteCharacters = atob(data.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: data.contentType });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = data.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Control matrix exported successfully');
    },
    onError: (error) => {
      toast.error('Failed to export control matrix');
      console.error('Export failed:', error);
    },
  });

  const handleExport = () => {
    exportMutation.mutate({
      frameworkId,
      frameworkName: framework?.name,
    });
  };

  // Handle delete framework
  const handleDeleteFramework = () => {
    if (deleteConfirmationText !== framework?.name) {
      toast.error('Please type the framework name correctly to confirm deletion');
      return;
    }
    deleteFrameworkMutation.mutate({ id: frameworkId });
  };

  // Control management handlers
  const handleCreateControl = () => {
    if (!controlFormData.controlId.trim()) {
      toast.error('Control ID is required');
      return;
    }
    if (!controlFormData.title.trim()) {
      toast.error('Control title is required');
      return;
    }
    if (!controlFormData.description.trim()) {
      toast.error('Control description is required');
      return;
    }
    createControlMutation.mutate({
      frameworkId,
      controlId: controlFormData.controlId,
      title: controlFormData.title,
      description: controlFormData.description,
    });
  };

  const _handleEditControl = (control: { id: string; controlId: string; title: string; description: string }) => {
    setEditingControl(control.id);
    setControlFormData({
      controlId: control.controlId,
      title: control.title,
      description: control.description,
    });
  };

  const handleUpdateControl = () => {
    if (!editingControl) return;
    if (!controlFormData.title.trim()) {
      toast.error('Control title is required');
      return;
    }
    if (!controlFormData.description.trim()) {
      toast.error('Control description is required');
      return;
    }
    updateControlMutation.mutate({
      id: editingControl,
      title: controlFormData.title,
      description: controlFormData.description,
    });
  };

  const _handleDeleteControl = async (id: string, controlId: string) => {
    if (
      window.confirm(
        `Are you sure you want to delete custom control "${controlId}"? This will also delete all associated evidence and tests for this workspace.`
      )
    ) {
      await deleteControlMutation.mutateAsync({ id });
    }
  };

  const handleTestClick = (statusId: string) => {
    navigate({
      to: '/workspaces/$workspaceId/compliance/tests/$testId',
      params: { workspaceId, testId: statusId },
      search: {
        from: 'tests',
        frameworkId,
        frameworkName: framework?.name,
      },
    });
  };

  return (
    <Main>
      <MainHeader />

      <div className="mx-auto box-content flex max-w-[1008px] flex-col px-6 pb-12">
        {/* Breadcrumb */}
        <div className="mb-4 flex items-center gap-2 text-sm">
          <Link
            to="/workspaces/$workspaceId/compliance"
            params={{ workspaceId }}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            Tests
          </Link>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
          <span className="text-foreground">
            {frameworkQuery.isLoading ? (
              <Skeleton className="h-4 w-24 inline-block" />
            ) : (
              framework?.name || 'Framework'
            )}
          </span>
        </div>

        {/* Header section */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <div className="text-foreground mb-2 text-2xl leading-6 font-semibold">
              {frameworkQuery.isLoading ? (
                <Skeleton className="h-8 w-48" />
              ) : (
                framework?.name || 'Framework Controls'
              )}
            </div>
            <div className="text-muted-foreground text-sm leading-5">
              Browse and manage controls for this compliance framework. View test status and coverage.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handleExport}
              disabled={exportMutation.isPending}
            >
              {exportMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              Export
            </Button>
            {!isViewer && (
              <>
                {framework?.canManage && (
                  <Button variant="outline" onClick={handleOpenEditFramework}>
                    <Pencil className="h-4 w-4" />
                    Edit
                  </Button>
                )}
                <Link
                  to="/workspaces/$workspaceId/compliance/$frameworkId/controls"
                  params={{ workspaceId, frameworkId }}
                >
                  <Button>
                    <Plus className="h-4 w-4" />
                    Manage Controls
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>

        {/* Stats Card */}
        <div className="mb-8 flex gap-4">
          {treeQuery.isLoading ? (
            <>
              {/* Skeleton: Test Status Card */}
              <div className="flex-1 rounded-xl border border-border/50 bg-card shadow-xs">
                <div className="p-6">
                  <div className="flex items-center gap-8">
                    {/* Skeleton: Pass Rate Donut */}
                    <Skeleton className="h-[100px] w-[100px] rounded-full flex-shrink-0" />
                    <div className="h-16 w-px bg-border" />
                    {/* Skeleton: Test Stats */}
                    <div className="flex-1 flex items-center justify-between gap-4">
                      {[...Array(5)].map((_, i) => (
                        <div key={i} className="text-center min-w-[60px]">
                          <Skeleton className="h-8 w-10 mx-auto mb-1" />
                          <Skeleton className="h-3 w-12 mx-auto" />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              {/* Skeleton: Assignment Card */}
              <div className="w-[280px] rounded-xl border border-border/50 bg-card shadow-xs">
                <div className="p-6">
                  <div className="flex items-center gap-6">
                    <Skeleton className="h-[100px] w-[100px] rounded-full flex-shrink-0" />
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-2">
                        <Skeleton className="h-2 w-2 rounded-full" />
                        <Skeleton className="h-4 w-16" />
                        <Skeleton className="h-4 w-6 ml-auto" />
                      </div>
                      <div className="flex items-center gap-2">
                        <Skeleton className="h-2 w-2 rounded-full" />
                        <Skeleton className="h-4 w-20" />
                        <Skeleton className="h-4 w-6 ml-auto" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Test Status Card */}
              <div className="flex-1 rounded-xl border border-border/50 bg-card shadow-xs">
                <div className="p-6">
                  <div className="flex items-center gap-8">
                    {/* Pass Rate Donut */}
                    <div className="relative flex-shrink-0" style={{ width: 100, height: 100 }}>
                      <svg width="100" height="100" viewBox="0 0 100 100" className="transform -rotate-90">
                        <circle
                          cx="50"
                          cy="50"
                          r="40"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="8"
                          className="text-muted/20"
                        />
                        {stats.tests.total > 0 && stats.tests.passed > 0 && (
                          <circle
                            cx="50"
                            cy="50"
                            r="40"
                            fill="none"
                            stroke="rgb(34, 197, 94)"
                            strokeWidth="8"
                            strokeDasharray={`${(stats.tests.passed / stats.tests.total) * 251.327} 251.327`}
                            strokeLinecap="round"
                            className="transition-all duration-500"
                          />
                        )}
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-xl font-semibold tabular-nums">
                          {stats.tests.total > 0 ? Math.round((stats.tests.passed / stats.tests.total) * 100) : 0}%
                        </span>
                        <span className="text-[10px] text-muted-foreground uppercase tracking-wide">Pass Rate</span>
                      </div>
                    </div>

                    <div className="h-16 w-px bg-border" />

                    {/* Test Stats */}
                    <div className="flex-1 flex items-center justify-between gap-4">
                      <button
                        type="button"
                        onClick={() => setSelectedStatus('all')}
                        className="text-center min-w-[60px] rounded-lg px-2 py-1 -mx-2 -my-1 hover:bg-muted/50 transition-colors cursor-pointer"
                      >
                        <div className="text-2xl font-semibold tabular-nums">{stats.tests.total}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">Total</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedStatus('PASSED')}
                        className="text-center min-w-[60px] rounded-lg px-2 py-1 -mx-2 -my-1 hover:bg-muted/50 transition-colors cursor-pointer"
                      >
                        <div className="flex items-center justify-center gap-1.5">
                          <span className="h-2 w-2 rounded-full bg-green-500" />
                          <span className="text-2xl font-semibold tabular-nums text-green-600 dark:text-green-500">{stats.tests.passed}</span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">Passed</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedStatus('FAILED')}
                        className="text-center min-w-[60px] rounded-lg px-2 py-1 -mx-2 -my-1 hover:bg-muted/50 transition-colors cursor-pointer"
                      >
                        <div className="flex items-center justify-center gap-1.5">
                          <span className="h-2 w-2 rounded-full bg-red-500" />
                          <span className="text-2xl font-semibold tabular-nums text-red-600 dark:text-red-500">{stats.tests.failed}</span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">Failed</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedStatus('NEEDS_REVIEW')}
                        className="text-center min-w-[60px] rounded-lg px-2 py-1 -mx-2 -my-1 hover:bg-muted/50 transition-colors cursor-pointer"
                      >
                        <div className="flex items-center justify-center gap-1.5">
                          <span className="h-2 w-2 rounded-full bg-amber-500" />
                          <span className="text-2xl font-semibold tabular-nums text-amber-600 dark:text-amber-500">{stats.tests.inReview}</span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">In Review</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedStatus('NOT_TESTED')}
                        className="text-center min-w-[60px] rounded-lg px-2 py-1 -mx-2 -my-1 hover:bg-muted/50 transition-colors cursor-pointer"
                      >
                        <div className="flex items-center justify-center gap-1.5">
                          <span className="h-2 w-2 rounded-full bg-muted-foreground/40" />
                          <span className="text-2xl font-semibold tabular-nums text-muted-foreground">{stats.tests.notStarted}</span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">Not Started</div>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Assignment Card */}
              <div className="w-[280px] rounded-xl border border-border/50 bg-card shadow-xs">
                <div className="p-6">
                  <div className="flex items-center gap-6">
                    {/* Assignment Donut */}
                    <div className="relative flex-shrink-0" style={{ width: 100, height: 100 }}>
                      <svg width="100" height="100" viewBox="0 0 100 100" className="transform -rotate-90">
                        <circle
                          cx="50"
                          cy="50"
                          r="40"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="8"
                          className="text-muted/20"
                        />
                        {stats.tests.total > 0 && stats.tests.assigned > 0 && (
                          <circle
                            cx="50"
                            cy="50"
                            r="40"
                            fill="none"
                            stroke="rgb(59, 130, 246)"
                            strokeWidth="8"
                            strokeDasharray={`${(stats.tests.assigned / stats.tests.total) * 251.327} 251.327`}
                            strokeLinecap="round"
                            className="transition-all duration-500"
                          />
                        )}
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-xl font-semibold tabular-nums">
                          {stats.tests.total > 0 ? Math.round((stats.tests.assigned / stats.tests.total) * 100) : 0}%
                        </span>
                        <span className="text-[10px] text-muted-foreground uppercase tracking-wide">Assigned</span>
                      </div>
                    </div>

                    {/* Assignment Stats */}
                    <div className="flex flex-1 min-w-0 flex-col gap-1">
                      <button
                        type="button"
                        onClick={() => setSelectedAssignee('assigned')}
                        className="flex items-center gap-2 rounded-md px-2 py-1 -mx-2 hover:bg-muted/50 transition-colors cursor-pointer"
                      >
                        <span className="h-2 w-2 shrink-0 rounded-full bg-primary" />
                        <span className="text-sm text-muted-foreground truncate">Assigned</span>
                        <span className="ml-auto shrink-0 font-semibold tabular-nums">{stats.tests.assigned}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedAssignee('unassigned')}
                        className="flex items-center gap-2 rounded-md px-2 py-1 -mx-2 hover:bg-muted/50 transition-colors cursor-pointer"
                      >
                        <span className="h-2 w-2 shrink-0 rounded-full bg-muted-foreground/30" />
                        <span className="text-sm text-muted-foreground truncate">Unassigned</span>
                        <span className="ml-auto shrink-0 font-semibold tabular-nums">{stats.tests.unassigned}</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* View Toggle */}
        <div className="sticky top-0 pt-4  bg-background z-10">
          <div className="mb-4 border-b border-border">
            <Tabs value={view} onValueChange={(v) => setView(v as 'tests' | 'controls')}>
              <TabsList className="h-auto p-0 bg-transparent gap-4">
                <TabsTrigger
                  value="tests"
                  className="px-1 pb-2 pt-0 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent bg-transparent shadow-none data-[state=active]:shadow-none"
                >
                  Tests
                </TabsTrigger>
                <TabsTrigger
                  value="controls"
                  className="px-1 pb-2 pt-0 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent bg-transparent shadow-none data-[state=active]:shadow-none"
                >
                  Controls
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          <TestsToolbar
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
            searchPlaceholder="Search by control ID or name..."
            selectedStatus={selectedStatus}
            onStatusChange={setSelectedStatus}
            statusOptions={statusOptions}
            leftContent={(
              <>
                {view === 'tests' && (
                  <TestTypeFilter
                    value={selectedTestType}
                    onChange={setSelectedTestType}
                  />
                )}
                <AssigneeFilter
                  value={selectedAssignee}
                  onChange={setSelectedAssignee}
                />
                <LabelFilter
                  value={selectedLabelIds}
                  onChange={setSelectedLabelIds}
                />
              </>
            )}
            rightContent={view === 'controls' ? (
              <>
                <Button
                  variant={hideNotApplicable ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setHideNotApplicable(!hideNotApplicable)}
                  className="h-9 px-3 text-muted-foreground"
                >
                  <EyeOff className="h-4 w-4" />
                  Hide N/A
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleToggleExpandAll}
                  className="h-9 px-3 text-muted-foreground"
                >
                  {isAllExpanded ? (
                    <>
                      <ChevronsDownUp className="h-4 w-4 " />
                      Collapse All
                    </>
                  ) : (
                    <>
                      <ChevronsUpDown className="h-4 w-4 " />
                      Expand All
                    </>
                  )}
                </Button>
              </>
            ) : null}
          />
        </div>
        {/* Tests list or Controls tree based on view */}
        {treeQuery.isLoading ? (
          <div className="space-y-1">
            {/* Skeleton rows matching TestsList layout */}
            {[...Array(8)].map((_, i) => (
              <div key={i} className="flex items-center gap-3 py-2 px-2">
                <Skeleton className="h-4 w-4 rounded" />
                <Skeleton className="h-4 w-4" />
                <Skeleton className="h-5 w-14 rounded" />
                <Skeleton className="h-4 flex-1 max-w-[300px]" />
                <div className="ml-auto flex items-center gap-3">
                  <Skeleton className="h-5 w-12 rounded" />
                  <Skeleton className="h-5 w-12 rounded" />
                  <Skeleton className="h-4 w-4" />
                  <Skeleton className="h-6 w-14 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : view === 'tests' ? (
          <TestsList
            tests={flatTests}
            onTestClick={handleTestClick}
            onAddTest={!isViewer ? () => setAddTestDrawerOpen(true) : undefined}
            highlightedStatusId={highlightedStatusId}
            isAdmin={isAdmin}
          />
        ) : (
          <ControlTree
            key={treeKey}
            ref={controlTreeRef}
            nodes={filteredTree}
            onAddTest={handleAddTest}
            onImportTemplate={handleImportTemplate}
            onMarkNotApplicable={handleMarkNotApplicable}
            onMarkApplicable={handleMarkApplicable}
            onTestClick={handleTestClick}
            hideNotApplicable={hideNotApplicable}
            hasActiveFilters={!!debouncedSearch || selectedStatus !== 'all' || selectedAssignee !== 'all' || selectedLabelIds.length > 0}
            highlightedStatusId={highlightedStatusId}
            initiallyExpanded={!!debouncedSearch || selectedStatus !== 'all' || selectedAssignee !== 'all'}
          />
        )}
      </div>

      {/* Create Test Drawer */}
      <AddTestDrawer
        open={addTestDrawerOpen}
        onOpenChange={setAddTestDrawerOpen}
        controlDefId={selectedControlDefId}
        frameworkId={frameworkId}
        onSuccess={handleAddTestSuccess}
      />

      {/* Import Test Drawer */}
      <ImportTemplateDrawer
        open={importTemplateDrawerOpen}
        onOpenChange={setImportTemplateDrawerOpen}
        controlDefId={importControlDefId}
        onSuccess={handleImportTemplateSuccess}
      />

      {/* Create/Edit Control Dialog */}
      <Dialog
        open={isCreateControlDialogOpen || editingControl !== null}
        onOpenChange={(open) => {
          if (!open) {
            setIsCreateControlDialogOpen(false);
            setEditingControl(null);
            setControlFormData({ controlId: '', title: '', description: '' });
          }
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingControl ? 'Edit Custom Control' : `Add ${isBuiltInFramework ? 'Custom ' : ''}Control`}
            </DialogTitle>
            <DialogDescription>
              {editingControl
                ? 'Update the custom control details. Control ID cannot be changed.'
                : isBuiltInFramework
                ? 'Add a workspace-specific custom control to this built-in framework. This will only be visible in your workspace.'
                : 'Add a new control to this custom framework.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="controlId">Control ID *</Label>
              <Input
                id="controlId"
                placeholder="e.g., CC6.1, AC-01"
                value={controlFormData.controlId}
                onChange={(e) => setControlFormData({ ...controlFormData, controlId: e.target.value })}
                disabled={!!editingControl}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Unique identifier for this control
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                placeholder="e.g., Logical Access Controls"
                value={controlFormData.title}
                onChange={(e) => setControlFormData({ ...controlFormData, title: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                placeholder="Describe the control objective and requirements..."
                rows={5}
                value={controlFormData.description}
                onChange={(e) => setControlFormData({ ...controlFormData, description: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsCreateControlDialogOpen(false);
                setEditingControl(null);
                setControlFormData({ controlId: '', title: '', description: '' });
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={editingControl ? handleUpdateControl : handleCreateControl}
              disabled={createControlMutation.isPending || updateControlMutation.isPending}
            >
              {editingControl
                ? updateControlMutation.isPending
                  ? 'Updating...'
                  : 'Update Control'
                : createControlMutation.isPending
                ? 'Adding...'
                : 'Add Control'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Framework Dialog */}
      <Dialog open={isEditFrameworkDialogOpen} onOpenChange={setIsEditFrameworkDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Framework</DialogTitle>
            <DialogDescription>
              Update the framework name and description.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-framework-name">Name *</Label>
              <Input
                id="edit-framework-name"
                value={editFrameworkName}
                onChange={(e) => setEditFrameworkName(e.target.value)}
                placeholder="Framework name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-framework-description">Description</Label>
              <Textarea
                id="edit-framework-description"
                value={editFrameworkDescription}
                onChange={(e) => setEditFrameworkDescription(e.target.value)}
                placeholder="Framework description (optional)"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
            <Button
              variant="destructive"
              onClick={() => {
                setIsEditFrameworkDialogOpen(false);
                setDeleteConfirmationText('');
                setIsDeleteFrameworkDialogOpen(true);
              }}
            >
              <Trash2 className="h-4 w-4" />
              Delete Framework
            </Button>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setIsEditFrameworkDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveFramework}
                disabled={updateFrameworkMutation.isPending}
              >
                {updateFrameworkMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Framework Confirmation Dialog */}
      <Dialog open={isDeleteFrameworkDialogOpen} onOpenChange={setIsDeleteFrameworkDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-destructive">Delete Framework</DialogTitle>
            <DialogDescription>
              This action cannot be undone. This will permanently delete the framework
              and all associated controls and tests.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              To confirm deletion, please type the framework name:{' '}
              <span className="font-semibold text-foreground">{framework?.name}</span>
            </p>
            <Input
              value={deleteConfirmationText}
              onChange={(e) => setDeleteConfirmationText(e.target.value)}
              placeholder="Type framework name to confirm"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteFrameworkDialogOpen(false);
                setDeleteConfirmationText('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteFramework}
              disabled={deleteConfirmationText !== framework?.name || deleteFrameworkMutation.isPending}
            >
              {deleteFrameworkMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Framework'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Main>
  );
}

export const Route = createFileRoute('/(protected)/workspaces/$workspaceId/compliance/$frameworkId/')({
  component: ControlsExplorerPage,
});
